// Composant Radar Chart Risques (HALTES) avec Recharts
// Echelle normalisee 0-8 pour equilibrer visuellement les axes
// Axes N/A (max=0) filtres automatiquement — le radar s'adapte

import { useState } from 'react'
import {
  RadarChart as RechartsRadar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Progress } from '@/components/ui/progress'

// Echelle fixe du radar Risques : 0 a 8
const RADAR_MAX_SCALE = 8

// Palette pour les radars multi-métiers (ordre cohérent : 1er = vert SCOP, 2e = bleu, 3e = orange)
const RADAR_SERIES_COLORS = ['#439C35', '#2563EB', '#D97706']

export interface ScoresAxes {
  H: number
  A: number
  LT: number
  E: number
  S: number
}

export interface MaxParAxe {
  H: number
  A: number
  LT: number
  E: number
  S: number
}

export interface RadarSeries {
  name: string                // libellé affiché en légende (ex: nom du métier)
  scores: ScoresAxes
  maxScores: MaxParAxe
  color?: string              // override couleur ; sinon palette par défaut
}

export interface RadarChartProps {
  // Mono-série (compat existante)
  scores?: ScoresAxes
  maxScores?: MaxParAxe
  // Multi-séries (story 11) — si fourni, prend le pas sur scores/maxScores
  series?: RadarSeries[]
  showLabels?: boolean
  height?: number
}

const AXES_CONFIG = [
  { key: 'H', label: 'Hygiène' },
  { key: 'A', label: 'Accès' },
  { key: 'LT', label: 'Livraisons\net Tâches' },
  { key: 'E', label: 'Énergies' },
  { key: 'S', label: 'Secours' },
]

// Fallback barres horizontales si < 3 axes evaluables
function FallbackBars({ data }: { data: Array<{ subject: string; rawScore: number; max: number }> }) {
  return (
    <div className="space-y-3 py-4">
      {data.map((item) => {
        const percent = item.max > 0 ? (item.rawScore / item.max) * 100 : 0
        return (
          <div key={item.subject}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-foreground">{item.subject.replace('\n', ' ')}</span>
              <span className="text-xs text-muted-foreground">{item.rawScore}/{item.max}</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        )
      })}
    </div>
  )
}

export default function RadarChart({
  scores,
  maxScores,
  series,
  showLabels = true,
  height = 300,
}: RadarChartProps) {
  // Mode multi-séries (story 11)
  if (series && series.length > 0) {
    return <MultiSeriesRadar series={series} showLabels={showLabels} height={height} />
  }

  // Mode mono (compat) — scores requis
  if (!scores) {
    return null
  }

  // Filtrer les axes dont max > 0 (evaluables uniquement)
  const activeAxes = AXES_CONFIG.filter((axe) => {
    const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
    return max > 0
  })

  // Construire les donnees uniquement pour les axes actifs
  const data = activeAxes.map((axe) => {
    const rawScore = scores[axe.key as keyof ScoresAxes] || 0
    const max = maxScores?.[axe.key as keyof MaxParAxe] || 1
    const normalizedScore = (rawScore / max) * RADAR_MAX_SCALE
    return {
      subject: axe.label,
      score: Math.round(normalizedScore * 10) / 10,
      rawScore,
      max,
      fullMark: RADAR_MAX_SCALE,
    }
  })

  // Pas assez d'axes pour un radar (< 3) → fallback en barres
  if (data.length < 3) {
    return <FallbackBars data={data} />
  }

  const radarColor = 'var(--primary)'

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RechartsRadar
          data={data}
          margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
        >
          <PolarGrid
            stroke="var(--border)"
            strokeWidth={1}
            gridType="polygon"
          />
          <PolarAngleAxis
            dataKey="subject"
            tick={({ x, y, payload }: any) => (
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="var(--muted-foreground)"
                style={{ fontSize: '11px', fontWeight: 500 }}
              >
                {payload.value.includes('\n') ? (
                  payload.value.split('\n').map((line: string, i: number) => (
                    <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))
                ) : (
                  payload.value
                )}
              </text>
            )}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, RADAR_MAX_SCALE]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="Score"
            dataKey="score"
            stroke={radarColor}
            fill={radarColor}
            fillOpacity={0.35}
            strokeWidth={3.5}
            dot={{
              r: 4,
              fill: radarColor,
              stroke: 'var(--card)',
              strokeWidth: 2,
            }}
          />
          {showLabels && (
            <Tooltip
              formatter={(_value: any, _name: any, entry: any) => {
                const item = entry.payload
                return [
                  `${item.score} / ${item.fullMark}`,
                  item.subject.replace('\n', ' '),
                ]
              }}
              contentStyle={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            />
          )}
        </RechartsRadar>
      </ResponsiveContainer>
    </div>
  )
}

// ── Multi-séries radar (story 11 — cumul des métiers sur le même graphe) ──
function MultiSeriesRadar({
  series,
  showLabels,
  height,
}: {
  series: RadarSeries[]
  showLabels: boolean
  height: number
}) {
  // US-31 : possibilité d'isoler un métier (« solo ») via la légende cliquable.
  // null = tous les métiers superposés (comportement par défaut).
  const [focusedSerie, setFocusedSerie] = useState<number | null>(null)

  // Axes actifs = ceux où au moins une série a max > 0
  const activeAxes = AXES_CONFIG.filter(axe =>
    series.some(s => (s.maxScores[axe.key as keyof MaxParAxe] || 0) > 0)
  )

  // Construire les datapoints : 1 ligne par axe, 1 colonne par série
  const data = activeAxes.map(axe => {
    const point: any = { subject: axe.label, fullMark: RADAR_MAX_SCALE }
    series.forEach((s, i) => {
      const rawScore = s.scores[axe.key as keyof ScoresAxes] || 0
      const max = s.maxScores[axe.key as keyof MaxParAxe] || 0
      // Si max=0 pour cette série sur cet axe → score=0 (sinon division par 0)
      const normalized = max > 0 ? (rawScore / max) * RADAR_MAX_SCALE : 0
      point[`s${i}`] = Math.round(normalized * 10) / 10
    })
    return point
  })

  if (data.length < 3) {
    return <FallbackBars data={data.map(d => ({ subject: d.subject, rawScore: 0, max: RADAR_MAX_SCALE }))} />
  }

  // Hauteur du radar : on garde la place pour la légende externe au-dessous
  const radarHeight = series.length > 1 ? height - 40 : height

  return (
    <div style={{ width: '100%' }}>
      {/* US-31 : sélecteur de métier au-dessus du radar */}
      {showLabels && series.length > 1 && (
        <MetierToggleLegend
          series={series}
          focused={focusedSerie}
          onChange={setFocusedSerie}
        />
      )}
      <div style={{ width: '100%', height: radarHeight }}>
        <ResponsiveContainer>
          <RechartsRadar
            data={data}
            margin={{ top: 20, right: 40, bottom: 10, left: 40 }}
          >
            <PolarGrid stroke="var(--border)" strokeWidth={1} gridType="polygon" />
            <PolarAngleAxis
              dataKey="subject"
              tick={({ x, y, payload }: any) => (
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="var(--muted-foreground)"
                  style={{ fontSize: '11px', fontWeight: 500 }}
                >
                  {payload.value.includes('\n') ? (
                    payload.value.split('\n').map((line: string, i: number) => (
                      <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>{line}</tspan>
                    ))
                  ) : payload.value}
                </text>
              )}
            />
            <PolarRadiusAxis angle={90} domain={[0, RADAR_MAX_SCALE]} tick={false} axisLine={false} />
            {series.map((s, i) => {
              const color = s.color ?? RADAR_SERIES_COLORS[i % RADAR_SERIES_COLORS.length]
              const isHidden = focusedSerie !== null && focusedSerie !== i
              return (
                <Radar
                  key={i}
                  name={s.name}
                  dataKey={`s${i}`}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.18}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: color, stroke: 'var(--card)', strokeWidth: 1.5 }}
                  hide={isHidden}
                />
              )
            })}
            {showLabels && (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              />
            )}
          </RechartsRadar>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// Version simplifiee pour mobile
export function RadarChartMini({ scores, maxScores }: { scores: ScoresAxes; maxScores?: MaxParAxe }) {
  return <RadarChart scores={scores} maxScores={maxScores} height={250} showLabels={false} />
}

// US-31 : légende cliquable des séries du radar — mode solo par métier.
// « Tous » + 1 bouton par métier. Click = isole ce métier ; re-click ou
// click sur « Tous » = ré-affiche toutes les séries.
function MetierToggleLegend({
  series,
  focused,
  onChange,
}: {
  series: RadarSeries[]
  focused: number | null
  onChange: (next: number | null) => void
}) {
  const baseBtn: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    lineHeight: '16px',
    border: '1px solid var(--border)',
    background: 'var(--background)',
    color: 'var(--foreground)',
    cursor: 'pointer',
    maxWidth: 220,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'background 0.15s, border-color 0.15s',
  }

  const allActive = focused === null
  return (
    <div
      role="toolbar"
      aria-label="Filtrer les métiers affichés sur le radar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 6,
        padding: '0 8px 8px',
      }}
    >
      <button
        type="button"
        onClick={() => onChange(null)}
        style={{
          ...baseBtn,
          background: allActive ? 'var(--primary)' : 'var(--background)',
          color: allActive ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
          borderColor: allActive ? 'var(--primary)' : 'var(--border)',
          fontWeight: allActive ? 600 : 400,
        }}
        aria-pressed={allActive}
      >
        Tous
      </button>
      {series.map((s, i) => {
        const color = s.color ?? RADAR_SERIES_COLORS[i % RADAR_SERIES_COLORS.length]
        const isFocused = focused === i
        const isDimmed = focused !== null && !isFocused
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(isFocused ? null : i)}
            title={s.name}
            aria-pressed={isFocused}
            style={{
              ...baseBtn,
              borderColor: isFocused ? color : 'var(--border)',
              background: isFocused ? color + '22' : 'var(--background)',
              opacity: isDimmed ? 0.55 : 1,
              fontWeight: isFocused ? 600 : 400,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                flexShrink: 0,
              }}
            />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.name}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// Composant legende personnalisee avec points
export function RadarLegend({ scores, maxScores, compact = false }: { scores: ScoresAxes; maxScores?: MaxParAxe; compact?: boolean }) {
  const getScoreColor = (score: number, max: number) => {
    if (max === 0) return 'text-muted-foreground bg-muted'
    const ratio = score / max
    if (ratio >= 0.7) return 'text-primary bg-primary/20'
    if (ratio >= 0.4) return 'text-primary bg-primary/10'
    return 'text-primary bg-primary/5'
  }

  if (compact) {
    return (
      <div className="grid grid-cols-5 gap-1 mt-2">
        {AXES_CONFIG.map((axe) => {
          const score = scores[axe.key as keyof ScoresAxes] || 0
          const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
          const isNA = max === 0
          return (
            <div
              key={axe.key}
              className={`py-1 px-1.5 rounded text-center ${getScoreColor(score, max)} ${isNA ? 'opacity-60' : ''}`}
            >
              <div className={`text-xs font-medium ${isNA ? '' : 'opacity-75'}`}>
                {axe.label.replace('\n', ' ')}
                {isNA && <span className="block text-xs">(N/A)</span>}
              </div>
              <div className="text-xs font-bold">{isNA ? '—' : score}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {AXES_CONFIG.map((axe) => {
        const score = scores[axe.key as keyof ScoresAxes] || 0
        const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
        const isNA = max === 0
        return (
          <div
            key={axe.key}
            className={`p-3 rounded-lg ${getScoreColor(score, max)} ${isNA ? 'opacity-60' : ''}`}
          >
            <div className={`text-xs font-medium ${isNA ? '' : 'opacity-75'}`}>
              {axe.label.replace('\n', ' ')}
              {isNA && <span className="ml-1 text-xs">(N/A)</span>}
            </div>
            <div className="text-lg font-bold">
              {isNA ? 'N/A' : `${score}/${max} pts`}
            </div>
          </div>
        )
      })}
    </div>
  )
}
